import { useState, useEffect, useRef } from "react";
import { Outlet, useLocation } from "react-router-dom";
import Lenis from "lenis";
import Header, { QuoteContext } from "./Header";
import Footer from "./Footer";
import WhatsAppButton from "./WhatsAppButton";
import QuoteDialog from "./QuoteDialog";
import MobileStickyCTA from "./MobileStickyCTA";
import { track, fireAdsConversion, GADS_CONVERSIONS } from "../../lib/analytics";
import { SITE } from "../../lib/config";

export default function Layout() {
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [quoteProduct, setQuoteProduct] = useState("");
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();
  const lenisRef = useRef(null);
  const rafRef = useRef(null);

  const startLenis = () => {
    if (lenisRef.current) return;
    const lenis = new Lenis({ duration: 1.2, smoothWheel: true });
    lenisRef.current = lenis;
    const loop = (t) => {
      lenis.raf(t);
      rafRef.current = requestAnimationFrame(loop);
    };
    rafRef.current = requestAnimationFrame(loop);
  };

  const stopLenis = () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    lenisRef.current?.destroy();
    lenisRef.current = null;
  };

  useEffect(() => {
    startLenis();
    return stopLenis;
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    lenisRef.current?.scrollTo(0, { immediate: true });
  }, [pathname]);

  useEffect(() => {
    // Fully destroy Lenis while the quote modal is open so its global touch
    // handlers don't block native scroll inside the modal on mobile.
    if (quoteOpen) stopLenis();
    else startLenis();
  }, [quoteOpen]);

  useEffect(() => {
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        setScrolled(window.scrollY > 40);
        ticking = false;
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Global click delegation — fires the Google Ads "WhatsApp Enquiry" and
  // "Call Click" conversions whenever the user taps any WhatsApp / phone
  // CTA on the site. One handler covers ALL current and future CTAs
  // (Header shortcut, floating WA button, sticky bar, hero phone link,
  // product page WA/Call links, per-size WA chips, PP colour chips,
  // wholesale/final CTAs, success-screen "Open WhatsApp", etc.).
  // Anchor default action is untouched so tel:/wa.me still open exactly
  // as before. Fires exactly once per click.
  useEffect(() => {
    const onDocClick = (e) => {
      if (!e.target || !e.target.closest) return;
      const waLink = e.target.closest('a[href*="wa.me"], a[href*="api.whatsapp.com"], a[href*="whatsapp.com/send"]');
      if (waLink) {
        fireAdsConversion(GADS_CONVERSIONS.whatsapp_click);
        return;
      }
      const telLink = e.target.closest('a[href^="tel:"]');
      if (telLink) {
        fireAdsConversion(GADS_CONVERSIONS.call_click, { value: 1.0, currency: "INR" });
      }
    };
    document.addEventListener("click", onDocClick, true);
    return () => document.removeEventListener("click", onDocClick, true);
  }, []);

  // Every "Get a Quote" / "Request a Quote" CTA on the site now goes
  // straight to WhatsApp with a product-specific message pre-filled —
  // no more form modal. Fires the Google Ads "WhatsApp Enquiry" conversion.
  const openQuote = (product = "") => {
    const name = typeof product === "string" ? product : product?.name || "";
    const cleanName = name.trim();
    const msg = cleanName
      ? `Hi Chennai Packs, I'd like a quote for ${cleanName}. Please share pricing and MOQ. Thank you.`
      : "Hi Chennai Packs, I'd like a quote for your packaging products. Please share pricing and MOQ. Thank you.";
    const waUrl = `https://wa.me/${SITE.whatsapp}?text=${encodeURIComponent(msg)}`;
    track("Quote Button Clicked", { product: cleanName || "general" });
    fireAdsConversion(GADS_CONVERSIONS.whatsapp_click);
    if (typeof window !== "undefined") {
      window.open(waUrl, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <QuoteContext.Provider value={openQuote}>
      <Header scrolled={scrolled} />
      <main className={`${scrolled ? "pt-[52px] sm:pt-[70px]" : "pt-[70px]"} pb-[72px] sm:pb-0 min-h-screen transition-[padding] duration-300`}>
        <Outlet />
      </main>
      <Footer />
      <WhatsAppButton />
      <MobileStickyCTA />
      <QuoteDialog open={quoteOpen} onOpenChange={setQuoteOpen} product={quoteProduct} />
    </QuoteContext.Provider>
  );
}
